// TutorSessionsByDateTable.jsx
import React, { useMemo, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Divider,
  Stack,
  Chip,
  useTheme,
  Select,
  Menu,
  MenuItem,
} from "@mui/material";
import { Schedule } from "@mui/icons-material";

const toLocalDateKey = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`; // YYYY-MM-DD
  };
  const makeFixedDateColumns = (days = 10) => {
    // Returns array for today + last `days` days (total days+1 columns)
    const out = [];
    const now = new Date();
    // Start at local today (no time component needed for labels/keys)
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    for (let i = 0; i <= days; i += 1) {
      const d = new Date(start);
      d.setDate(start.getDate() - i);
      out.push({
        key: toLocalDateKey(d),
        date: d,
        label: d.toLocaleDateString(undefined, { month: "short", day: "numeric" }), // e.g., "Oct 12"
        sublabel: d.toLocaleDateString(undefined, { weekday: "short" }), // e.g., "Sun"
      });
    }
    // Current day first, then back in time
    return out.reverse();
  };
  
  export default function TutorSessionsByDateTable({ data }) {
    const theme = useTheme();
    const [dates, setDates] = useState(10);
    const rows = Array.isArray(data) ? data : Array.isArray(data) ? data : [];
  
    // Normalize incoming rows (derive local dateKey)
    const normalized = useMemo(() => {
      return rows.map((r) => {
        const d = new Date(r.session_date);
        const dateKey = toLocalDateKey(d);
        return {
          tutor_id: r.tutor_id,
          first_name: r.first_name,
          last_name: r.last_name,
          dateKey,
          dateObj: d,
          program: r.program || "Unknown",
          student_count: Number(r.student_count) || 0,
        };
      });
    }, [rows]);
  
    // Fixed columns: today + last 10 dates (always present)
    const dateColumns = useMemo(() => makeFixedDateColumns(dates), [dates]);
  
    // Group by tutor → by date → { total, byProgram }
    const tutors = useMemo(() => {
      const m = new Map();
      for (const r of normalized) {
        if (!m.has(r.tutor_id)) {
          m.set(r.tutor_id, {
            tutor_id: r.tutor_id,
            first_name: r.first_name,
            last_name: r.last_name,
            byDate: new Map(), // dateKey -> { total, byProgram: Map(program -> count) }
          });
        }
        const t = m.get(r.tutor_id);
        if (!t.byDate.has(r.dateKey)) {
          t.byDate.set(r.dateKey, { total: 0, byProgram: new Map() });
        }
        const slot = t.byDate.get(r.dateKey);
        slot.total += r.student_count;
        slot.byProgram.set(r.program, (slot.byProgram.get(r.program) || 0) + r.student_count);
      }
      return Array.from(m.values()).sort((a, b) => {
        const na = String(a.tutor_name), nb = String(b.tutor_name);
        const n = na.localeCompare(nb);
        if (n !== 0) return n;
        return (a.tutor_id ?? 0) - (b.tutor_id ?? 0);
      });
    }, [normalized]);
  
    // Stable colors per program
    const programColor = (() => {
      const palette = [
        theme.palette.primary.main,
        theme.palette.secondary.main,
        theme.palette.success.main,
        theme.palette.info.main,
        theme.palette.warning.main,
        theme.palette.error.main,
      ];
      const map = new Map();
      let i = 0;
      return (name) => {
        if (!map.has(name)) {
          map.set(name, palette[i % palette.length]);
          i += 1;
        }
        return map.get(name);
      };
    })();
  
    return (
      <Box sx={{ p: 1 }}>
        {/* Header */}
        <Box sx={{ mb: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box>
              <Typography variant="h6" fontWeight="bold">
                Tutor sessions 
              </Typography>
              <Typography variant="body2" color="text.secondary">
                &nbsp;View session submissions
              </Typography>
            </Box>
          </Stack>
          <Divider sx={{ mt: 1 }} />
        </Box>
  
        <Paper variant="outlined" sx={{ borderRadius: 2 }}>
          <TableContainer sx={{ maxHeight: "70vh" }}>
            <Table stickyHeader size="small" aria-label="Tutor sessions by fixed recent dates">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 160 }}>
                    <Typography variant="subtitle2">Tutor</Typography>
                  </TableCell>
                  {dateColumns && dateColumns.map((dc, index) => (
                    <TableCell key={dc.key} align="center" sx={{ minWidth: 140 }}>
                      <Typography fontWeight={index === dateColumns.length -1 ? 'bold': ''} variant="subtitle2">{dc.label}</Typography>
                      <Typography fontWeight={index === dateColumns.length -1 ? 'bold': ''} variant="caption" color="text.secondary">
                        {dc.sublabel}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
  
              <TableBody>
                {tutors.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={1 + dateColumns.length}>
                      <Box
                        sx={{
                          py: 6,
                          display: "grid",
                          placeItems: "center",
                          color: "text.secondary",
                        }}
                      >
                        <Typography variant="body2">No data to display</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  tutors.map((t) => (
                    <TableRow key={t.tutor_id} hover>
                      <TableCell sx={{
                            position: 'sticky',
                            left: 0,
                            zIndex: 1, // Adjust zIndex as needed
                            background: 'theme.palette.background.paper', // Or theme.palette.background.paper
                          }}>
                        <Typography variant="body2" fontWeight={600}>
                          {t.first_name + " " + t.last_name}
                        </Typography>
                      </TableCell>
  
                      {dateColumns.map((dc) => {
                        const slot = t.byDate.get(dc.key);
                        if (!slot) {
                          return (
                            <TableCell key={dc.key} align="center" sx={{ color: "text.disabled" }}>
                              <Typography variant="caption">Missing</Typography>
                            </TableCell>
                          );
                        }
                        return (
                          <TableCell key={dc.key} align="center" sx={{ px: 1.25, py: 0.75 }}>
                            <Stack spacing={0.5} alignItems="center">
                              <Typography variant="body2" fontWeight={700}>
                                {slot.total} students
                              </Typography>
                              <Stack
                                direction="row"
                                spacing={0.5}
                                useFlexGap
                                flexWrap="wrap"
                                justifyContent="center"
                              >
                                {Array.from(slot.byProgram.entries()).map(([prog, count]) => (
                                  <Chip
                                    key={prog}
                                    size="small"
                                    label={`${prog}: ${count}`}
                                    sx={{
                                      bgcolor: programColor(prog),
                                      color: theme.palette.getContrastText(programColor(prog)),
                                      height: 22,
                                    }}
                                  />
                                ))}
                              </Stack>
                            </Stack>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
  
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
          Showing last 
        </Typography>
        <Select
          value={dates}
          onChange={(e) => setDates(e.target.value)}
          size="small"
        >
              
            <MenuItem key={10} value={10}>
                <Typography>Last 10 days</Typography>
            </MenuItem>
            <MenuItem value={20} key={20}>
                <Typography>Last 20 days</Typography>
            </MenuItem>
            <MenuItem key={30} value={30}>
                <Typography>Last 30 days</Typography>
            </MenuItem>
            <MenuItem value={60} key={60}>
                <Typography>Last 60 days</Typography>
            </MenuItem>
        </Select>

      </Box>
    );
  }